import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './AiGovernance.css';
import AiGovNav from './AiGovNav';
import { AI_FRAMEWORKS, getScopeStatement, getModelCards, getModelCard, downloadBundle, openHtmlReport,
  listAimsDocuments, adoptAimsDocument } from './aiGovApi';

/** D3 UI — AIMS scope statement + per-asset model cards / AI impact assessments. */
export default function AiGovDocuments() {
  const { t } = useTranslation();
  const [frameworkId, setFrameworkId] = useState(AI_FRAMEWORKS[0].id);
  const [scope, setScope] = useState(null);
  const [cards, setCards] = useState([]);
  const [aimsDocs, setAimsDocs] = useState([]);
  const [adopting, setAdopting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openCard, setOpenCard] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c, d] = await Promise.all([
        getScopeStatement(frameworkId).catch(() => null),
        getModelCards().catch(() => []),
        listAimsDocuments().catch(() => []),
      ]);
      setScope(s);
      setCards(c);
      setAimsDocs(d);
    } finally {
      setLoading(false);
    }
  }, [frameworkId]);

  useEffect(() => { load(); }, [load]);

  const adopt = async (type) => {
    setAdopting(type);
    try {
      await adoptAimsDocument(type, frameworkId);
      setAimsDocs(await listAimsDocuments().catch(() => aimsDocs));
    } finally {
      setAdopting(null);
    }
  };

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('AIGovernance.documentsTitle', 'AIMS Documents')}</h1>
        <p className="aig-subtitle">{t('AIGovernance.documentsSubtitle', 'Scope statement and per-model cards / AI impact assessments.')}</p>
      </div>

      <AiGovNav />

      <div className="aig-toolbar">
        <select className="aig-btn" value={frameworkId} onChange={(e) => setFrameworkId(Number(e.target.value))}>
          {AI_FRAMEWORKS.map((f) => <option key={f.id} value={f.id}>{f.displayName}</option>)}
        </select>
        <div className="aig-spacer" />
        <button className="aig-btn" onClick={() => openHtmlReport(frameworkId)}>{t('AIGovernance.exportPdf', 'Export report (PDF)')}</button>
        <button className="aig-btn aig-btn-primary" onClick={() => downloadBundle(frameworkId)}>{t('AIGovernance.exportBundle', 'Export bundle (.zip)')}</button>
      </div>

      {loading ? (
        <div className="aig-spinner" />
      ) : (
        <>
          {scope && (
            <div className="aig-card" style={{ marginBottom: '1.25rem' }}>
              <h3 className="aig-card-title">{t('AIGovernance.scopeStatement', 'AIMS Scope Statement')} — {scope.orgName}</h3>
              <p style={{ fontSize: '0.85rem', color: '#334155' }}>{scope.context}</p>
              <p style={{ fontSize: '0.85rem', color: '#334155' }}><strong>{t('AIGovernance.boundaries', 'Boundaries')}:</strong> {scope.boundaries}</p>
              <p style={{ fontSize: '0.85rem', color: '#334155' }}><strong>{t('AIGovernance.exclusionsLabel', 'Exclusions')}:</strong> {scope.exclusions}</p>
              <div className="aig-card-row"><span>{t('AIGovernance.inScopeAssets', 'In-scope AI systems')}</span><strong>{scope.inScopeAssetCount}</strong></div>
              <div className="aig-card-row"><span>{t('AIGovernance.highRisk', 'High-risk (EU AI Act)')}</span><strong>{scope.highRiskCount}</strong></div>
              {scope.providers && scope.providers.length > 0 && (
                <div className="aig-card-row"><span>{t('AIGovernance.providers', 'Providers')}</span><span>{scope.providers.join(', ')}</span></div>
              )}
            </div>
          )}

          {/* AIMS documents — generate + finalize (IN_FORCE) to satisfy the management-system controls */}
          <div className="aig-card" style={{ marginBottom: '1.25rem' }}>
            <h3 className="aig-card-title">{t('AIGovernance.aimsDocuments', 'AIMS Documents')}</h3>
            <p className="aig-muted" style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>
              {t('AIGovernance.aimsDocsNote', 'Generate and adopt each AI Management System document. Adopting marks it in-force and satisfies its ISO 42001 / NIST AI RMF control.')}
            </p>
            <table className="aig-table">
              <thead><tr>
                <th>{t('AIGovernance.document', 'Document')}</th>
                <th>{t('AIGovernance.status', 'Status')}</th>
                <th />
              </tr></thead>
              <tbody>
                {aimsDocs.map((d) => (
                  <tr key={d.type}>
                    <td>{d.title}</td>
                    <td>
                      <span className={`aig-badge ${d.inForce ? 'aig-b-pass' : 'aig-b-fail'}`}>
                        {d.inForce ? t('AIGovernance.adopted', 'Adopted') : t('AIGovernance.notAdopted', 'Not adopted')}
                      </span>
                    </td>
                    <td>
                      <button className="aig-btn aig-btn-primary" disabled={adopting === d.type} onClick={() => adopt(d.type)}>
                        {adopting === d.type
                          ? t('AIGovernance.adopting', 'Generating…')
                          : d.inForce ? t('AIGovernance.regenerate', 'Regenerate') : t('AIGovernance.generateAdopt', 'Generate & adopt')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="aig-card-title">{t('AIGovernance.modelCards', 'Model cards')}</h3>
          {cards.length === 0 ? (
            <div className="aig-empty">{t('AIGovernance.noModelCards', 'No governed AI assets yet — register one in the inventory.')}</div>
          ) : (
            <table className="aig-table">
              <thead><tr>
                <th>{t('AIGovernance.name', 'Name')}</th><th>{t('AIGovernance.type', 'Type')}</th>
                <th>{t('AIGovernance.provider', 'Provider')}</th><th>{t('AIGovernance.riskTier', 'Risk tier')}</th>
                <th>{t('AIGovernance.lifecycle', 'Lifecycle')}</th><th /></tr></thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.assetId}>
                    <td>{c.name}</td><td>{c.type}</td><td>{c.provider || '—'}</td>
                    <td>{c.riskTier || '—'}</td><td>{c.lifecycleStage || '—'}</td>
                    <td><button className="aig-btn" onClick={() => getModelCard(c.assetId).then(setOpenCard)}>{t('AIGovernance.viewCard', 'View card')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {openCard && <ModelCardModal t={t} card={openCard} onClose={() => setOpenCard(null)} />}
    </div>
  );
}

function ModelCardModal({ t, card, onClose }) {
  const d = card.modelDetails || {};
  const sections = [
    ['intendedUse', 'Intended use'],
    ['outOfScopeUse', 'Out-of-scope use'],
    ['factors', 'Relevant factors'],
    ['metrics', 'Metrics'],
    ['evaluationData', 'Evaluation data'],
    ['trainingData', 'Training data'],
    ['ethicalConsiderations', 'Ethical considerations'],
    ['caveatsAndRecommendations', 'Caveats & recommendations'],
  ];
  return (
    <div className="aig-overlay" onClick={onClose}>
      <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{d.name} — {t('AIGovernance.modelCard', 'Model card')}</h3>
        <div className="aig-card-row"><span>{t('AIGovernance.type', 'Type')}</span><span>{d.type}</span></div>
        <div className="aig-card-row"><span>{t('AIGovernance.provider', 'Provider')}</span><span>{d.provider || '—'}</span></div>
        <div className="aig-card-row"><span>{t('AIGovernance.riskTier', 'Risk tier')}</span><span>{d.riskTier || '—'}</span></div>
        <div className="aig-card-row"><span>{t('AIGovernance.sensitivity', 'Data sensitivity')}</span><span>{d.dataSensitivity || '—'}</span></div>
        <div className="aig-card-row"><span>{t('AIGovernance.purpose', 'Purpose')}</span><span>{d.purpose || '—'}</span></div>
        <p style={{ fontSize: '0.82rem', color: '#475569', marginTop: '0.75rem' }}><strong>{t('AIGovernance.impactSummary', 'AI impact assessment')}:</strong> {card.impactSummary}</p>
        {sections.map(([k, label]) => (
          <div key={k} className="aig-card-row" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <span style={{ fontWeight: 600 }}>{t('AIGovernance.mc_' + k, label)}</span>
            <span className={card[k] ? '' : 'aig-muted'}>{card[k] || t('AIGovernance.notDocumented', 'Not documented')}</span>
          </div>
        ))}
        {card.linkedRisks && card.linkedRisks.length > 0 && (
          <p style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>{t('AIGovernance.linkedRisks', 'Linked risks')}: {card.linkedRisks.length}</p>
        )}
        <div className="aig-modal-actions">
          <button className="aig-btn aig-btn-primary" onClick={onClose}>{t('AIGovernance.close', 'Close')}</button>
        </div>
      </div>
    </div>
  );
}
